import React, { Component, type ReactNode, type ErrorInfo } from 'react'
import { AlertTriangle, RefreshCw, RotateCcw, Home, ChevronDown, ChevronUp } from 'lucide-react'

export interface ErrorBoundaryProps {
  children?: ReactNode
  fallback?: ReactNode | ((props: { error: Error | null; resetError: () => void }) => ReactNode)
  onReset?: () => void
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  title?: string
  message?: string
  showHomeButton?: boolean
}

export interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  showDetails: boolean
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo })
    this.props.onError?.(error, errorInfo)
    console.error('[ErrorBoundary caught error]:', error, errorInfo)
  }

  resetError = (): void => {
    this.props.onReset?.()
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    })
  }

  toggleDetails = (): void => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }))
  }

  handleReload = (): void => {
    window.location.reload()
  }

  handleGoHome = (): void => {
    window.location.href = '/'
  }

  override render(): ReactNode {
    const { hasError, error, errorInfo, showDetails } = this.state
    const { children, fallback, title, message, showHomeButton = true } = this.props

    if (hasError) {
      if (typeof fallback === 'function') {
        return fallback({ error, resetError: this.resetError })
      }

      if (fallback) {
        return fallback
      }

      return (
        <div className="min-h-[300px] w-full flex items-center justify-center p-6 my-auto">
          <div className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl border border-red-200 dark:border-red-900/50 shadow-lg p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6" />
            </div>

            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
              {title || 'Algo salió mal al cargar esta sección'}
            </h3>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {message || error?.message || 'Se produjo un error inesperado al procesar la información.'}
            </p>

            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={this.resetError}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl bg-primary-600 hover:bg-primary-700 text-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
              >
                <RotateCcw className="w-4 h-4" />
                Reintentar
              </button>

              <button
                type="button"
                onClick={this.handleReload}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/60 text-gray-700 dark:text-gray-200 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Recargar página
              </button>

              {showHomeButton && (
                <button
                  type="button"
                  onClick={this.handleGoHome}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/60 text-gray-700 dark:text-gray-200 transition-colors"
                >
                  <Home className="w-4 h-4" />
                  Ir al inicio
                </button>
              )}
            </div>

            {error && (
              <div className="mt-6 border-t border-gray-100 dark:border-gray-800 pt-4 text-left">
                <button
                  type="button"
                  onClick={this.toggleDetails}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
                >
                  {showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  {showDetails ? 'Ocultar detalles técnicos' : 'Ver detalles técnicos'}
                </button>

                {showDetails && (
                  <div className="mt-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 font-mono text-xs text-red-600 dark:text-red-400 overflow-x-auto max-h-48 whitespace-pre-wrap">
                    <p className="font-bold">{error.name}: {error.message}</p>
                    {error.stack && <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">{error.stack}</p>}
                    {errorInfo?.componentStack && (
                      <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">{errorInfo.componentStack}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )
    }

    return children
  }
}

/**
 * HOC helper to wrap any component with an ErrorBoundary
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>,
): React.FC<P> {
  const ComponentWithErrorBoundary: React.FC<P> = (props: P) => {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    )
  }

  ComponentWithErrorBoundary.displayName = `WithErrorBoundary(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`

  return ComponentWithErrorBoundary
}

export default ErrorBoundary
